import { useEffect, useRef, useState } from "react";
import { SquarePen } from "lucide-react";
import {
  Alert,
  Button,
  EmptyState,
  Switch,
  Table,
  Tabs,
  Text,
  type Column,
} from "@marekl/adminer/components";
import { apiErrorMessage } from "@marekl/adminer/api";
import { useT } from "@marekl/adminer/hooks";
import {
  useUpdateDisciplineCategories,
  type AgeCategory,
  type DeductionTable,
  type Discipline,
  type DisciplineCategory,
  type Floor,
  type Recording,
  type FormationSize,
  type PerformanceClass,
  type ScoringSheet,
} from "../../../lib/queries/rules";
import { describeOverrides } from "../../../lib/categoryDiff";
import { CategoryEditModal } from "./CategoryEditModal";
import styles from "./CategoryMatrix.module.css";
import type { ID } from "../../../lib/id";

/**
 * The age × formation grid of one discipline, for one performance tier
 * at a time.
 *
 * A cell is a combination. Enabling one is the common edit by far — most
 * of the work is deciding which combinations are competed at all — so
 * the switch acts directly; everything else opens the editor. The matrix
 * is sparse on purpose, so a disabled cell is the normal state, not a
 * gap to fill.
 *
 * Cells show what they change rather than what they are: nearly every
 * one inherits the discipline defaults, so printing the resolved values
 * everywhere would bury the handful that actually deviate.
 *
 * Selecting arbitrary cells and editing them together is deliberately
 * absent: it belongs to the multirow-actions primitive coming to
 * @marekl/adminer, and a local selection toolbar would be the bespoke
 * duplicate that lands us with two of them. Whole rows and whole columns
 * are edited from the header and the row action, which need no
 * selection model.
 */
export function CategoryMatrix({
  discipline,
  readOnly,
  ageCategories,
  formationSizes,
  performanceClasses,
  deductionTables,
  scoringSheets,
  floors,
  recordings,
}: {
  discipline: Discipline;
  readOnly: boolean;
  ageCategories: AgeCategory[];
  formationSizes: FormationSize[];
  performanceClasses: PerformanceClass[];
  deductionTables: DeductionTable[];
  scoringSheets: ScoringSheet[];
  floors: Floor[];
  recordings: Recording[];
}) {
  const t = useT();
  const all = discipline.categories ?? [];
  const [classId, setClassId] = useState(() => performanceClasses[0]?.id ?? "");
  const [editing, setEditing] = useState<DisciplineCategory[] | null>(null);
  const save = useUpdateDisciplineCategories(discipline.id);
  const errorRef = useRef<HTMLDivElement>(null);

  // Scrolled to, not merely rendered. The matrix is a wide grid and the
  // class switch sits below it, so a refusal — most often „na kombinace,
  // které by tím zanikly, už odkazuje N přihlášek" — was reported at the
  // top of a page the admin had already scrolled past, and the toggle
  // simply appeared to do nothing. Same rule the season-registration
  // submit follows: a failure must be visible from the control that
  // failed.
  //
  // Above the early returns below, or the hook order changes between
  // renders.
  useEffect(() => {
    if (save.error) errorRef.current?.scrollIntoView({ block: "center" });
  }, [save.error]);

  // The grid stays two-dimensional and the tier is a switch above it.
  // A third visual dimension would double the rows for a value that is
  // constant while you work through one tier.
  const active = performanceClasses.some((c) => c.id === classId)
    ? classId
    : (performanceClasses[0]?.id ?? "");
  const categories = all.filter((c) => c.performance_class_id === active);

  const cellFor = (ageId: ID, sizeId: ID) =>
    categories.find((c) => c.age_category_id === ageId && c.formation_size_id === sizeId);

  const setEnabled = (targets: DisciplineCategory[], enabled: boolean) => {
    if (targets.length === 0) return;
    save.mutate({ category_ids: targets.map((t) => t.id), enabled });
  };

  /** Cells of one tier, across the whole age × formation grid. */
  const cellsOfClass = (id: ID) => all.filter((c) => c.performance_class_id === id);

  /** A tier is competed when any of its combinations is. */
  const isCompeted = (id: ID) => cellsOfClass(id).some((c) => c.enabled);

  if (ageCategories.length === 0 || formationSizes.length === 0 || performanceClasses.length === 0) {
    return (
      <EmptyState
        title="Matice je prázdná"
        description="Disciplína nemá v některé ze svých tří tabulek žádný řádek, takže nevznikla ani jedna kombinace."
      />
    );
  }

  /**
   * The edit affordance an axis header carries. Rows and columns get the
   * identical control — both are "edit everything along this axis", and
   * giving one a row action and the other a header button would make the
   * same operation look like two different features.
   */
  const axisEdit = (label: string, targets: DisciplineCategory[]) =>
    readOnly ? null : (
      <Button
        size="sm"
        weight="ghost"
        aria-label={label}
        title={label}
        disabled={targets.length === 0}
        onClick={() => setEditing(targets)}
      >
        <SquarePen aria-hidden="true" />
      </Button>
    );

  const columns: Column<AgeCategory>[] = [
    {
      key: "age",
      header: "Věková kategorie",
      tdClass: styles.axisColumn,
      render: (age) => (
        <>
          <span className={styles.axisHeader}>
            <Text variant="mono">{age.code}</Text>
            {axisEdit(
              `Upravit řádek ${age.name}`,
              categories.filter((c) => c.age_category_id === age.id),
            )}
          </span>
          <div>
            <Text variant="muted">{age.name}</Text>
          </div>
        </>
      ),
    },
    ...formationSizes.map<Column<AgeCategory>>((size) => ({
      key: `size-${size.id}`,
      tdClass: styles.cellColumn,
      header: (
        <span className={styles.axisHeader}>
          {size.name}
          {axisEdit(
            `Upravit sloupec ${size.name}`,
            categories.filter((c) => c.formation_size_id === size.id),
          )}
        </span>
      ),
      render: (age) => {
        const cell = cellFor(age.id, size.id);
        if (!cell) return <Text variant="muted">—</Text>;
        // A cell shows only what it changes — the time included. It
        // used to print the resolved limit unconditionally, which meant
        // every cell in the grid carried a line that was the discipline
        // default in all but a handful of them; the exceptions were the
        // thing worth seeing and they did not stand out.
        const diffs = describeOverrides(discipline, cell, {
          deductionTables,
          scoringSheets,
          floors,
          recordings,
        });
        return (
          <div className={styles.cell}>
            <Switch
              label={cell.enabled ? "Povoleno" : "Zakázáno"}
              checked={cell.enabled}
              disabled={readOnly || save.isPending}
              onChange={(e) => setEnabled([cell], e.target.checked)}
            />
            {/* Every line reads the same way — "Label: value", one
                face — so the eye can scan a column instead of
                re-parsing each entry. A cell with no lines inherits
                everything, which is the common case. */}
            {diffs.map((d) => (
              <Text
                key={d.key}
                variant="muted"
                className={styles.diff}
                title={`${d.label}: ${d.to}\nVýchozí: ${d.from}`}
              >
                {d.label}: {d.to} <span aria-label="Má vlastní nastavení">•</span>
              </Text>
            ))}
            {!readOnly && (
              <Button
                size="sm"
                variant="primary" weight="outline"
                onClick={() => setEditing([cell])}
                aria-label={`Upravit kombinaci ${age.name} × ${size.name}`}
              >
                Upravit
              </Button>
            )}
          </div>
        );
      },
    })),
  ];

  return (
    <>
      {/* The sentence is the SERVER's, verbatim. A second Czech copy here
          would drift from httperr and then tell the admin something the
          server does not do — the failure staleCopy.ts exists to prevent. */}
      <div ref={errorRef}>
        {save.error && <Alert variant="err">{apiErrorMessage(save.error, t)}</Alert>}
      </div>

      {performanceClasses.length > 1 && (
        <Tabs
          tabs={performanceClasses.map((c) => ({
            id: String(c.id),
            label: isCompeted(c.id) ? c.name : `${c.name} (nesoutěží se)`,
          }))}
          value={String(active)}
          onChange={(next: string) => setClassId(next)}
          aria-label="Výkonnostní třída"
        />
      )}

      {!readOnly && (
        <Switch
          label={
            isCompeted(active)
              ? "Tato výkonnostní třída se soutěží"
              : "Tato výkonnostní třída se nesoutěží"
          }
          checked={isCompeted(active)}
          disabled={save.isPending}
          onChange={(e) => setEnabled(cellsOfClass(active), e.target.checked)}
          hint="Vypnutím zakážete všechny kombinace této třídy naráz."
        />
      )}

      <Table<AgeCategory>
        framed
        rows={ageCategories}
        rowKey={(age) => age.id}
        columns={columns}
        isLoading={save.isPending}
      />

      <Text variant="muted">
        Buňky ukazují jen to, čím se liší od výchozího nastavení disciplíny; ostatní
        hodnoty dědí. Celý řádek nebo sloupec upravíte akcí v jeho záhlaví
        {performanceClasses.length > 1 ? ", vždy v právě zobrazené výkonnostní třídě." : "."}
      </Text>

      {editing && editing.length > 0 && (
        <CategoryEditModal
          key={editing.map((c) => c.id).join("-")}
          open
          discipline={discipline}
          targets={editing}
          deductionTables={deductionTables}
          scoringSheets={scoringSheets}
          floors={floors}
          recordings={recordings}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
